import { Emitter, type Event } from '@patsnap/tahiti-core';

export interface RawDocumentModification {
    document_id?: string;
    action?: string;
    data_id?: string;
    element_id?: string;
    content?: string;
    old_content?: string;
}

export interface DocumentModificationProgressEvent {
    eventId?: string;
    conversationId?: string;
    documentId?: string;
    modifications?: RawDocumentModification[];
    count?: number;
    status: 'streaming' | 'applied';
}

export interface DocumentModificationStreamEvent {
    changeId: string;
    index: number;
    phase?: 'anchor' | 'content';
    action?: string;
    old: string;
    new: string;
    dataId?: string | null;
    isComplete: boolean;
    eventId: string;
    conversationId: string;
    documentId?: string;
}

export interface DocumentModificationCompleteEvent {
    eventId: string;
    conversationId: string;
    modifications: RawDocumentModification[];
    documentId?: string;
    mode?: 'agent' | 'copilot';
    toolCallId?: string;
}

/** 文档修改会话的只读订阅边界。 */
export interface DocumentModificationEventSource {
    readonly onProgress: Event<DocumentModificationProgressEvent>;
    readonly onStream: Event<DocumentModificationStreamEvent>;
    readonly onComplete: Event<DocumentModificationCompleteEvent>;
}

/** 文档修改会话的发布边界。 */
export interface DocumentModificationEventPublisher {
    /** 发布修改进度。 */
    publishProgress(event: DocumentModificationProgressEvent): void;
    /** 发布实时修改片段。 */
    publishStream(event: DocumentModificationStreamEvent): void;
    /** 发布修改完成事实。 */
    publishComplete(event: DocumentModificationCompleteEvent): void;
}

/** 创建应用级、可隔离测试的文档修改事件通道。 */
export function createDocumentModificationEvents():
DocumentModificationEventSource & DocumentModificationEventPublisher {
    const progressEmitter = new Emitter<DocumentModificationProgressEvent>();
    const streamEmitter = new Emitter<DocumentModificationStreamEvent>();
    const completeEmitter = new Emitter<DocumentModificationCompleteEvent>();
    return {
        onProgress: progressEmitter.event,
        onStream: streamEmitter.event,
        onComplete: completeEmitter.event,
        publishProgress: event => progressEmitter.fire(event),
        publishStream: event => streamEmitter.fire(event),
        publishComplete: event => completeEmitter.fire(event),
    };
}

export const documentModificationEvents = createDocumentModificationEvents();
